import { authenticatedApi } from "@/api";
import { HomeIcon } from "@/components/icons";
import { useAuth } from "@/context/AuthContext";
import { API } from "@nextgammon/shared";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export function ProfileScreen() {
  const navigate = useNavigate();
  const { username, userId, token } = useAuth();
  const [profile, setProfile] = useState<API.User.ProfileResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const authApi = authenticatedApi(token);

        // Fetch profile data
        const profileRes = await authApi.v1.user.profile.$get();
        if (profileRes.ok) {
          const data = await profileRes.json();
          setProfile(data);
        } else {
          throw new Error("Failed to fetch profile");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load profile");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [token]);

  return (
    <div className="w-full h-screen overflow-hidden bg-gradient-to-b from-indigo-900 to-purple-900 text-white relative flex flex-col">
      {/* Fixed Navigation */}
      <div className="fixed top-4 left-4 z-10">
        <button
          onClick={() => navigate("/")}
          className="w-12 h-12 bg-black bg-opacity-30 hover:bg-opacity-50 rounded-full flex items-center justify-center text-white transition-all"
        >
          <HomeIcon size={20} />
        </button>
      </div>

      {/* Main Content - Side by Side Layout */}
      <div
        className="flex-1 overflow-y-auto px-6 py-20 pb-6"
        style={{ overscrollBehaviorY: "contain" }}
      >
        <div className="flex gap-8 h-full">
          {/* Left Side - Profile Info */}
          <div className="flex-shrink-0 w-80 flex flex-col items-center justify-start">
            {/* Profile Avatar */}
            <div className="w-32 h-32 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center text-4xl font-bold shadow-lg">
              {username ? username.charAt(0).toUpperCase() : "?"}
            </div>

            <h2 className="mt-4 text-2xl font-bold text-center">{username || "Anonymous"}</h2>
            <p className="text-indigo-300 text-sm mt-1">
              User ID: {userId?.substring(0, 8) || "Unknown"}
            </p>

            {profile && (
              <div className="mt-6 bg-indigo-700 bg-opacity-50 px-6 py-4 rounded-lg">
                <p className="text-sm text-indigo-300 text-center">Current Balance</p>
                <p className="text-3xl font-bold text-yellow-400 text-center">
                  {profile.coins.toLocaleString()}
                </p>
                <p className="text-sm text-yellow-400 text-center">coins</p>
              </div>
            )}

            {/* Loading and Error states */}
            {isLoading && (
              <div className="mt-6 text-center">
                <p className="text-indigo-300">Loading profile...</p>
              </div>
            )}

            {error && (
              <div className="mt-6 bg-red-600 bg-opacity-20 text-red-300 p-4 rounded-lg">
                <p className="text-sm">{error}</p>
              </div>
            )}
          </div>

          {/* Right Side - Game Statistics */}
          {profile && (
            <div className="flex-1 bg-indigo-800 bg-opacity-50 rounded-xl p-4 overflow-hidden">
              <h3 className="text-xl font-bold mb-4">Game Statistics</h3>
              <div className="grid grid-cols-3 gap-4 auto-rows-fr">
                <div className="bg-indigo-700 bg-opacity-50 p-4 rounded-lg min-h-0">
                  <p className="text-indigo-300 text-sm mb-2">Games Played</p>
                  <p className="text-2xl font-bold truncate">{profile.stats.gamesPlayed}</p>
                </div>
                <div className="bg-indigo-700 bg-opacity-50 p-4 rounded-lg min-h-0">
                  <p className="text-indigo-300 text-sm mb-2">Wins</p>
                  <p className="text-2xl font-bold text-green-400 truncate">
                    {profile.stats.gamesWon}
                  </p>
                </div>
                <div className="bg-indigo-700 bg-opacity-50 p-4 rounded-lg min-h-0">
                  <p className="text-indigo-300 text-sm mb-2">Win Rate</p>
                  <p className="text-2xl font-bold text-blue-400 truncate">
                    {profile.stats.winRate}%
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileScreen;
